/**
 * Rendition planning + framework-free image header parsing.
 *
 * Kept free of any Supabase/Worker imports so it can be unit-tested and reused
 * by other upload paths (bulk import, authenticity docs).
 *
 * Why renditions at all: vendors in Bangladesh upload 4–8 MB phone photos, and
 * buyers browse on 360px screens over mobile data. Serving the original in a
 * 96px grid tile wastes their data budget. We therefore derive a small set of
 * WebP renditions server-side at upload time, so the browser never has to
 * download the original for a thumbnail.
 */

/**
 * `card` is legacy: it is no longer produced (the 480px `tile` covers phones
 * and `full` covers everything above it), but historic rows still carry it, so
 * the name stays in the type and in every lookup fallback.
 */
export type RenditionName = "thumb" | "tile" | "card" | "full";

export type RenditionSpec = {
  name: RenditionName;
  /** Longest-edge box the image is fitted into (aspect ratio preserved). */
  width: number;
  height: number;
  /** WebP quality 20–100. Smaller renditions tolerate more compression. */
  quality: number;
};

/**
 * Three sizes, chosen from what the buyer-facing cards actually measure:
 *  - thumb: wizard grid tile / cart + order line item (<=160 CSS px @2x)
 *  - tile:  2-up product grid on a 360-430px phone (~165-200 CSS px @2x)
 *  - full:  desktop card, search results and product detail hero (<=576 CSS px @2x)
 * The old 640px `card` tier sat between `tile` and `full` and was picked by
 * almost nothing: dropping it removes one stored object per photo (a 25% cut
 * in object count) with no surface losing pixels it was painting.
 * Anything larger falls back to the original object.
 */
export const RENDITION_SPECS: readonly RenditionSpec[] = [
  { name: "thumb", width: 320, height: 320, quality: 70 },
  { name: "tile", width: 480, height: 480, quality: 72 },
  { name: "full", width: 1152, height: 1152, quality: 80 },
] as const;


/**
 * Wire format a rendition object was stored in. WebP is the floor every
 * browser we serve can decode; AVIF is kept only when the transform endpoint
 * actually returned it *and* it beat WebP on bytes for that same size.
 */
export type RenditionFormat = "webp" | "avif";

export const RENDITION_CONTENT_TYPES: Record<RenditionFormat, string> = {
  webp: "image/webp",
  avif: "image/avif",
};

export type RenditionRecord = {
  name: RenditionName;
  url: string;
  path: string;
  width: number;
  height: number;
  bytes: number;
  /**
   * Mime of the stored object. Optional because rows written before AVIF
   * support existed have no such field — those are WebP by construction.
   */
  contentType?: string;
};

/** Mime for a rendition row, defaulting legacy rows to WebP. */
export function renditionContentType(rec: Pick<RenditionRecord, "contentType">): string {
  const ct = typeof rec.contentType === "string" ? rec.contentType.trim().toLowerCase() : "";
  return ct === "image/avif" ? "image/avif" : "image/webp";
}

/** Parse a response content-type into a format we are willing to store. */
export function renditionFormatFromContentType(ct: string | null | undefined): RenditionFormat {
  return (ct ?? "").toLowerCase().includes("image/avif") ? "avif" : "webp";
}

/** Renditions keyed by name; absent keys mean that size was not produced. */
export type RenditionMap = Partial<Record<RenditionName, RenditionRecord>>;

/**
 * Derived object path for a rendition, e.g.
 * `shop/1720-ab12-brake.jpg` -> `shop/renditions/1720-ab12-brake@thumb.webp`.
 * The extension follows the stored format so the object never lies about its
 * bytes; `webp` stays the default because optimistic client-side URL guessing
 * (see `derivedRenditionUrl`) can only assume the baseline format.
 */
export function renditionPath(
  originalPath: string,
  name: RenditionName,
  format: RenditionFormat = "webp",
): string {
  const slash = originalPath.lastIndexOf("/");
  const dir = slash === -1 ? "" : originalPath.slice(0, slash + 1);
  const file = slash === -1 ? originalPath : originalPath.slice(slash + 1);
  const base = file.replace(/\.[^.]+$/, "");
  return `${dir}renditions/${base}@${name}.${format}`;
}


export type ImageSize = { width: number; height: number };

/**
 * Read intrinsic pixel dimensions straight from the file header.
 * Supports JPEG (SOFn scan), PNG (IHDR) and WEBP (VP8/VP8L/VP8X).
 * Returns null when the header cannot be understood — callers must treat
 * unknown dimensions as "skip downscaling", never as an error.
 */
export function readImageSize(bytes: Uint8Array): ImageSize | null {
  if (bytes.length < 24) return null;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

  // PNG — IHDR is always the first chunk at offset 16.
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
    return { width: view.getUint32(16), height: view.getUint32(20) };
  }

  // WEBP — RIFF container, chunk type at offset 12.
  if (
    bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46 &&
    bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50
  ) {
    const fourcc = String.fromCharCode(bytes[12]!, bytes[13]!, bytes[14]!, bytes[15]!);
    if (fourcc === "VP8 " && bytes.length > 30) {
      return {
        width: view.getUint16(26, true) & 0x3fff,
        height: view.getUint16(28, true) & 0x3fff,
      };
    }
    if (fourcc === "VP8L" && bytes.length > 25) {
      const bits = view.getUint32(21, true);
      return { width: (bits & 0x3fff) + 1, height: ((bits >> 14) & 0x3fff) + 1 };
    }
    if (fourcc === "VP8X" && bytes.length > 30) {
      const w = bytes[24]! | (bytes[25]! << 8) | (bytes[26]! << 16);
      const h = bytes[27]! | (bytes[28]! << 8) | (bytes[29]! << 16);
      return { width: w + 1, height: h + 1 };
    }
    return null;
  }

  // AVIF — ISO-BMFF; the primary item's size lives in an `ispe` box.
  if (
    bytes[4] === 0x66 && bytes[5] === 0x74 && bytes[6] === 0x79 && bytes[7] === 0x70
  ) {
    const limit = Math.min(bytes.length - 12, 4096);
    for (let i = 0; i < limit; i += 1) {
      if (
        bytes[i] === 0x69 && bytes[i + 1] === 0x73 && bytes[i + 2] === 0x70 && bytes[i + 3] === 0x65
      ) {
        // box payload: 4 bytes version/flags, then uint32 width, uint32 height.
        const w = view.getUint32(i + 8);
        const h = view.getUint32(i + 12);
        if (w > 0 && h > 0 && w < 100000 && h < 100000) return { width: w, height: h };
      }
    }
    return null;
  }

  // JPEG — walk markers until a start-of-frame carries the dimensions.
  if (bytes[0] === 0xff && bytes[1] === 0xd8) {
    let i = 2;
    while (i + 9 < bytes.length) {
      if (bytes[i] !== 0xff) {
        i += 1;
        continue;
      }
      const marker = bytes[i + 1]!;
      // Standalone markers carry no length payload.
      if (marker === 0xd8 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
        i += 2;
        continue;
      }
      const len = view.getUint16(i + 2);
      const isSof =
        marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc;
      if (isSof) return { width: view.getUint16(i + 7), height: view.getUint16(i + 5) };
      if (len < 2) return null;
      i += 2 + len;
    }
  }
  return null;
}

/** Fit `size` inside the spec box without upscaling. */
export function fitInside(size: ImageSize, spec: RenditionSpec): ImageSize {
  const scale = Math.min(spec.width / size.width, spec.height / size.height, 1);
  return {
    width: Math.max(1, Math.round(size.width * scale)),
    height: Math.max(1, Math.round(size.height * scale)),
  };
}

/* ------------------------------------------------------------------ *
 * Frame geometry — the server-authoritative half of the crop step.
 *
 * The wizard's crop dialog lets a vendor frame to 1:1 or 4:5 and renders the
 * result in the browser. That render is advisory: a crafted upload (or an
 * older client, or bulk import) can still deliver a 16:9 workbench shot. Since
 * every buyer surface paints onto a fixed canvas, an off-ratio source shows up
 * as a tiny part floating in dead space.
 *
 * So the *renditions* — the copies buyers actually download — are always
 * produced at one of the canonical frames, centre-cropped when the source
 * disagrees. The original object is kept untouched for zoom/authenticity.
 * ------------------------------------------------------------------ */

/** Canonical buyer-facing frames. Mirrors `CROP_ASPECTS` in image-crop.ts. */
export const FRAME_ASPECTS = { square: 1, portrait: 4 / 5 } as const;
export type FrameAspect = keyof typeof FRAME_ASPECTS;
export const DEFAULT_FRAME: FrameAspect = "square";

/**
 * How far a source may drift from a canonical ratio before we re-frame it.
 * 2% absorbs rounding from the browser's letterbox/compress pass (a 1600×1599
 * JPEG is square in every way that matters) without letting real 4:3 shots
 * through unframed.
 */
export const FRAME_TOLERANCE = 0.02;

export function isFrameAspect(v: unknown): v is FrameAspect {
  return v === "square" || v === "portrait";
}

/** Width / height of a source. */
export function aspectOf(size: ImageSize): number {
  return size.height > 0 ? size.width / size.height : 1;
}

/** True when the source already sits on `aspect` within tolerance. */
export function matchesFrame(size: ImageSize, aspect: FrameAspect): boolean {
  const target = FRAME_ASPECTS[aspect];
  return Math.abs(aspectOf(size) - target) / target <= FRAME_TOLERANCE;
}

/** Whichever canonical frame is closest to the source's own ratio. */
export function nearestFrame(size: ImageSize): FrameAspect {
  const a = aspectOf(size);
  let best: FrameAspect = DEFAULT_FRAME;
  let bestDelta = Number.POSITIVE_INFINITY;
  for (const key of Object.keys(FRAME_ASPECTS) as FrameAspect[]) {
    const delta = Math.abs(a - FRAME_ASPECTS[key]);
    if (delta < bestDelta) {
      bestDelta = delta;
      best = key;
    }
  }
  return best;
}

export type ResolvedFrame = {
  aspect: FrameAspect;
  /** true when the source did not already match `aspect` and must be cropped. */
  enforced: boolean;
  /** Why this frame was chosen — surfaced in warnings/telemetry. */
  source: "client" | "derived" | "default";
};

/**
 * Decide the frame the renditions must be produced at.
 *
 * A client hint is honoured only when it names a canonical frame; it is never
 * trusted to describe the pixels, so `enforced` is still computed from the
 * real header dimensions. With no hint (bulk import, legacy client) we snap to
 * the nearest canonical frame, which is a no-op for already-square photos.
 */
export function resolveFrame(
  size: ImageSize | null,
  requested?: string | null,
): ResolvedFrame {
  const hinted = isFrameAspect(requested) ? requested : null;
  if (!size) {
    return { aspect: hinted ?? DEFAULT_FRAME, enforced: false, source: hinted ? "client" : "default" };
  }
  const aspect = hinted ?? nearestFrame(size);
  return {
    aspect,
    enforced: !matchesFrame(size, aspect),
    source: hinted ? "client" : "derived",
  };
}

/* --------------------------------------------------------------------
 * Vendor framing hint (audit trail)
 * ------------------------------------------------------------------ */

/**
 * What the vendor did in the crop step, recorded verbatim for audit.
 *
 * This is client-reported and therefore untrusted: it never decides how the
 * pixels are produced (that is `resolveFrame` + a server-side `cover` crop).
 * It exists so a vendor or admin looking at a badly framed listing can tell
 * whether the vendor cropped it that way or the server re-framed an off-frame
 * source.
 */
export type VendorFrameHint = {
  /** Frame the vendor picked in the dialog. */
  requested: FrameAspect;
  /** Clockwise rotation applied, in degrees (0/90/180/270). */
  rotation: number;
  /** Fine straightening angle applied on top of the quarter turn, in degrees. */
  tilt?: number;
  /** Zoom factor, 1 = fit. */
  zoom: number;
  /** Crop centre as a fraction of the rotated image (0..1), when cropped. */
  centerX?: number;
  centerY?: number;
  /** false when the vendor skipped framing and the original bytes were sent. */
  cropApplied: boolean;
  /** Mirroring applied to the framed result. */
  flipX?: boolean;
  flipY?: boolean;
  /** "Paper white" background-cleanup strength, 0..1. */
  paper?: number;
  /** ISO timestamp of the vendor's confirmation. */
  at?: string;
};


const ALLOWED_ROTATIONS = [0, 90, 180, 270];

/** Coerce an untrusted payload into a storable hint, or null. */
export function sanitizeFrameHint(raw: unknown): VendorFrameHint | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (!isFrameAspect(r["requested"])) return null;
  const rotation = Number(r["rotation"]);
  const zoom = Number(r["zoom"]);
  const tilt = Number(r["tilt"]);
  const frac = (v: unknown): number | undefined => {
    const n = Number(v);
    return Number.isFinite(n) ? Math.min(1, Math.max(0, Number(n.toFixed(4)))) : undefined;
  };
  const cx = frac(r["centerX"]);
  const cy = frac(r["centerY"]);
  const at = typeof r["at"] === "string" ? r["at"].slice(0, 40) : undefined;
  return {
    requested: r["requested"],
    rotation: ALLOWED_ROTATIONS.includes(rotation) ? rotation : 0,
    ...(Number.isFinite(tilt) && tilt !== 0
      ? { tilt: Math.min(15, Math.max(-15, Number(tilt.toFixed(2)))) }
      : {}),
    zoom: Number.isFinite(zoom) ? Math.min(8, Math.max(1, Number(zoom.toFixed(3)))) : 1,
    ...(cx === undefined ? {} : { centerX: cx }),
    ...(cy === undefined ? {} : { centerY: cy }),
    cropApplied: r["cropApplied"] === true,
    ...(r["flipX"] === true ? { flipX: true } : {}),
    ...(r["flipY"] === true ? { flipY: true } : {}),
    ...(Number.isFinite(Number(r["paper"])) && Number(r["paper"]) > 0
      ? { paper: Math.min(1, Math.max(0, Number(Number(r["paper"]).toFixed(2)))) }
      : {}),
    ...(at ? { at } : {}),
  };
}



/** Largest box of `aspect` that fits inside a spec's square box. */
export function frameBox(spec: RenditionSpec, aspect: FrameAspect): ImageSize {
  const ratio = FRAME_ASPECTS[aspect];
  const width = Math.max(1, Math.round(Math.min(spec.width, spec.height * ratio)));
  const height = Math.max(1, Math.round(width / ratio));
  return { width, height };
}

/**
 * Output dimensions of a framed rendition: the source is centre-cropped to
 * `aspect`, then fitted into the spec box without upscaling.
 */
export function framedSize(source: ImageSize, spec: RenditionSpec, aspect: FrameAspect): ImageSize {
  const ratio = FRAME_ASPECTS[aspect];
  const cropW = Math.min(source.width, source.height * ratio);
  const cropH = cropW / ratio;
  const box = frameBox(spec, aspect);
  const scale = Math.min(box.width / cropW, box.height / cropH, 1);
  return {
    width: Math.max(1, Math.round(cropW * scale)),
    height: Math.max(1, Math.round(cropH * scale)),
  };
}


/**
 * Which renditions are worth producing for a source of the given size.
 * A rendition whose box already exceeds the source is skipped — re-encoding a
 * 400px photo at 1280px only adds storage and latency for zero benefit.
 */
export function planRenditions(size: ImageSize | null): RenditionSpec[] {
  if (!size) return [...RENDITION_SPECS];
  const longest = Math.max(size.width, size.height);
  const useful = RENDITION_SPECS.filter((s) => Math.max(s.width, s.height) < longest);
  // Always keep at least the smallest size so a thumbnail exists for any image.
  return useful.length ? useful : [RENDITION_SPECS[0]!];
}

/**
 * How far below the ideal pixel count a rendition may sit and still be chosen.
 * A tile asking for 340px is served the 320px copy: the 6% upscale is
 * invisible at phone density, while the alternative (the next step up) is
 * typically twice the bytes. Kept deliberately tight so real softness — a
 * 320px copy in a 640px slot — never ships.
 */
export const RENDITION_UNDERSHOOT_TOLERANCE = 0.9;

/** Best rendition record for a target CSS width, or null when none fits. */
export function pickRenditionRecord(
  renditions: RenditionMap | null | undefined,
  targetCssWidth: number,
  dpr = 2,
): RenditionRecord | null {
  if (!renditions) return null;
  const ratio = Math.min(2, Math.max(1, Number.isFinite(dpr) ? dpr : 2));
  const needed = targetCssWidth * ratio * RENDITION_UNDERSHOOT_TOLERANCE;
  // Legacy `card` rows are still honoured, between `tile` and `full`.
  const order: RenditionName[] = ["thumb", "tile", "card", "full"];
  for (const name of order) {
    const rec = renditions[name];
    if (rec && Math.max(rec.width, rec.height) >= needed) return rec;
  }
  return renditions.full ?? renditions.card ?? renditions.tile ?? renditions.thumb ?? null;
}

/** Best rendition URL for a target CSS width, falling back to the original. */
export function pickRendition(
  renditions: RenditionMap | null | undefined,
  targetCssWidth: number,
  originalUrl: string,
  /** Device pixel ratio to serve for; clamped to 1–2 (past 2x is imperceptible). */
  dpr = 2,
): string {
  return pickRenditionRecord(renditions, targetCssWidth, dpr)?.url ?? originalUrl;
}

/**
 * Same choice as `pickRendition`, but also reports the stored mime so a caller
 * can emit `<source type=...>` around it. The mime is only meaningful when a
 * rendition was actually chosen; falling back to the original reports null
 * because the original's format is not tracked here.
 */
export function pickRenditionSource(
  renditions: RenditionMap | null | undefined,
  targetCssWidth: number,
  originalUrl: string,
  dpr = 2,
): { url: string; contentType: string | null } {
  const rec = pickRenditionRecord(renditions, targetCssWidth, dpr);
  return rec
    ? { url: rec.url, contentType: renditionContentType(rec) }
    : { url: originalUrl, contentType: null };
}


